import type { NextPage } from 'next'
import Image from 'next/image'
import Link from 'next/link'
import React, { useEffect, useRef, useState } from 'react'
import { VideoJsPlayer, VideoJsPlayerOptions } from 'video.js'
// import { request as requestIdleCallbackShim } from 'requestidlecallback'

import { useTimeout, useIsomorphicLayoutEffect } from 'usehooks-ts'

import Contact from '../../components/contact/Contact'
import Item, { ItemProps } from '../../components/item/Item'
import ImageQuote from '../../components/imagequote/ImageQuote'
import Grid from '../../components/grid/Grid'
import Button from '../../components/button/Button'
import Careers from '../../components/careers/Careers'
import FadeIn from '../../components/fadeIn/FadeIn'
import VideoJSPlayer from '../../components/videoJSPlayer/VideoJSPlayer'
import CustomImage from '../../components/customImage/CustomImage'
import styles from '../../styles/pages/Batteries.module.scss'
import variables from '../../styles/_constants.module.scss'

import CustomMetaData from '../../components/general/customMetaData/CustomMetaData'

import { GenericPageLayoutProps } from '../../components/general/genericPageLayout/GenericPageLayout'

import { useGlobalContentContext } from '../../context/GlobalContentContext'

import { parseGenericPage } from '../../utils/parsers'
import { getAPI, createMarkup } from '../../utils/helpers'
import { useIsMobile } from '../../hooks/useIsDesktop'

import { useWindowSize } from 'usehooks-ts'

// Import Swiper React components
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper'

// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'

import { gsap } from 'gsap/dist/gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'

if (typeof window !== 'undefined') {
  // Due to SSR we can only register when we are in the browser
  gsap.registerPlugin(ScrollTrigger)
}

type Props = GenericPageLayoutProps

const SolidStateBatteries = (props: Props) => {
  const { setData } = useGlobalContentContext()
  const playerElRef = useRef<any>(null)
  const playerRef = useRef<any>(null)
  const playerContainerRef = useRef<any>(null)
  const columnRef = useRef<any>(null)
  const [imagesLoaded, setImagesLoaded] = useState(false)

  useEffect(() => {
    setData(props.globalData)
  }, [props.globalData, setData])

  const isMobile = useIsMobile()

  useIsomorphicLayoutEffect(() => {
    function render() {
      if (keyframes.frame % 2) {
        context.clearRect(0, 0, canvas.width, canvas.height)
        context.drawImage(images[keyframes.frame], 0, 0)
      }
    }

    const canvas = playerRef.current
    if (!canvas) return
    const context = canvas.getContext('2d')

    canvas.width = 1280
    canvas.height = 1280
    const startingFrame: number = 90
    const frameCount: number = 180

    const currentFrame = (index: number) => {
      const offsetIndex: number =
        index + frameCount / 2 >= frameCount
          ? index + Math.floor(frameCount / 2) - frameCount
          : index + Math.floor(frameCount / 2)

      const imagefile: string = (offsetIndex + 1).toString()
      const imagePrefix: string = offsetIndex.toString()
      return `/battery/Sakuu_Battery-animation_01${imagefile.padStart(
        4,
        '0'
      )}.jpg`
    }

    const images = new Array<HTMLImageElement>()

    const keyframes = {
      frame: 0,
    }

    for (let i = 0; i <= frameCount; i++) {
      const img = new window.Image()
      const iOffset =
        startingFrame + i >= frameCount
          ? startingFrame + i - frameCount
          : startingFrame + i
      img.src = currentFrame(iOffset)
      images.push(img)
    }

    const t1 = gsap.timeline({
      scrollTrigger: {
        trigger: playerElRef.current,
        start: () => {
          return isMobile ? 'top bottom' : 'top 50%'
        },
        end: () => {
          const columnHeight =
            columnRef.current.getBoundingClientRect().height -
            playerRef.current.getBoundingClientRect().height * 0.6
          return isMobile ? 'bottom top' : `+=${Math.abs(columnHeight)}px`
        },
        ease: 'linear',
        scrub: 2,
      },
      onUpdate: render,
    })

    t1.to(keyframes, {
      frame: frameCount - 1,
      snap: 'frame',
      ease: 'none',
      duration: 1,
    })

    images[0].onload = () => {
      ScrollTrigger.refresh()
      const ctx = render()
      setImagesLoaded(true)
    }

    return () => {
      t1 && t1.kill()
    }
  }, [isMobile])

  // set top of the canvas object
  const [offsetHeight, setOffsetHeight] = useState<string>('0')
  const { width, height } = useWindowSize()
  useIsomorphicLayoutEffect(() => {
    if (playerRef.current) {
      const playerHeight = playerRef.current.getBoundingClientRect().height

      const navHeight =
        height > 800
          ? parseInt(variables.headerSmallHeight) *
            parseInt(variables.rootFontBaseSize)
          : 0

      setOffsetHeight(`${(height + navHeight - playerHeight) / 2}px`)
    }
  }, [height, imagesLoaded])

  return (
    <div className={styles.container}>
      <CustomMetaData
        title={props.originalResponse.meta_title}
        description={props.originalResponse.meta_description}
        image={props.originalResponse.meta_image}
      />

      <div className={styles.main}>
        <section className={styles.hero}>
          <div className={styles.heroMaxWidthContainer}>
            <h1
              className={styles.heroTitle}
              dangerouslySetInnerHTML={createMarkup(
                props.originalResponse.hero.title
              )}
            ></h1>
            <h2 className={styles.heroSubtitle}>
              {props.originalResponse.hero.sub_title}
            </h2>

            <div
              className={styles.heroDetail}
              dangerouslySetInnerHTML={createMarkup(
                props.originalResponse.hero.detail
              )}
            ></div>
          </div>
          <div className={styles.heroImage}>
            <CustomImage
              src={props.originalResponse.hero}
              layout="responsive"
              priority
            />
          </div>
          <div className={styles.heroFootNotContainer}>
            <div className={styles.heroMaxWidthContainer}>
              <div
                className={styles.heroFootNote}
                dangerouslySetInnerHTML={createMarkup(
                  props.originalResponse.hero.shipping
                )}
              ></div>
            </div>
          </div>
        </section>

        {props.originalResponse.hero_items?.length > 0 && (
          <section className={styles.heroItems}>
            {props.originalResponse.hero_items
              .filter((itemA: ItemProps, i: number) => i % 4 == 0)
              .map((itemB: ItemProps, j: number) => (
                <FadeIn key={j} onMobile={false}>
                  <Grid columns={4}>
                    {props.originalResponse.hero_items
                      .filter(
                        (itemC: ItemProps, k: number) =>
                          k >= j * 4 && k < j * 4 + 4
                      )
                      .map((item: ItemProps, index: number) => (
                        <Item
                          key={index}
                          {...item}
                          id={j * 4 + index + 1}
                          hasBorder={j == 0}
                        />
                      ))}
                  </Grid>
                </FadeIn>
              ))}
          </section>
        )}
        {props.originalResponse.aboutsection && (
          <section className={styles.aboutSection}>
            {props.originalResponse.aboutSection.title && (
              <h2 className={styles.aboutSectionTitle}>
                {props.originalResponse.aboutsection.title}
              </h2>
            )}
            <div
              className={styles.aboutSectionDetail}
              dangerouslySetInnerHTML={createMarkup(
                props.originalResponse.aboutsection.detail
              )}
            ></div>

            <div className={styles.aboutSectionContainer}>
              <div className={styles.aboutSectionGrid}>
                <div className={styles.aboutSectionItemImage} ref={playerElRef}>
                  <div
                    className={styles.canvasContainer}
                    ref={playerContainerRef}
                    style={{
                      top: offsetHeight,
                    }}
                  >
                    <canvas ref={playerRef} />
                  </div>
                </div>
                <div className={styles.aboutSectionItemContent} ref={columnRef}>
                  {props.originalResponse.about_section_blocks.map(
                    (item: any, index: number) => (
                      <div key={item.id} className={styles.aboutSectionItem}>
                        <h3 className={styles.aboutSectionItemTitle}>
                          {item.title}
                        </h3>

                        <div
                          className={styles.aboutSectionItemDetail}
                          dangerouslySetInnerHTML={createMarkup(item.detail)}
                        ></div>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {props.originalResponse.graphsection && (
          <section className={styles.graphSection}>
            <h3 className={styles.graphSectionTitle}>
              {props.originalResponse.graphsection.title}
            </h3>
            <div
              className={styles.graphSectionDetail}
              dangerouslySetInnerHTML={createMarkup(
                props.originalResponse.graphsection.detail
              )}
            ></div>
            <div className={styles.graphSectionImage}>
              <CustomImage
                layout="responsive"
                src={props.originalResponse.graphsection}
              />
            </div>
          </section>
        )}

        {props.originalResponse.highlight_blocks?.length > 0 && (
          <section className={styles.highlightBlocks}>
            <div className={styles.highlightBlocksContainer}>
              {props.originalResponse.highlight_blocks.map(
                (item: any, index: number) => (
                  <div className={styles.highlightBlockItem} key={item.id}>
                    <FadeIn onMobile={true} onDesktop={false}>
                      <h2 className={styles.highlightBlockItemTitle}>
                        {item.title}
                      </h2>
                      <div
                        className={styles.highlightBlockItemDetail}
                        dangerouslySetInnerHTML={createMarkup(item.detail)}
                      ></div>
                    </FadeIn>
                  </div>
                )
              )}
            </div>
          </section>
        )}

        {props.originalResponse.transformativesection && (
          <section className={styles.transformativeSection}>
            <h2 className={styles.transformativeSectionTitle}>
              {props.originalResponse.transformativesection.title}
            </h2>
            <div
              className={styles.transformativeSectionDetail}
              dangerouslySetInnerHTML={createMarkup(
                props.originalResponse.transformativesection.detail
              )}
            ></div>

            <div className={styles.transformativeSectionBlocks}>
              <Grid columns={4}>
                {props.originalResponse.transformative_section_blocks.map(
                  (item: any, index: number) => (
                    <div
                      className={styles.transformativeSectionBlockItem}
                      key={item.id}
                    >
                      <h4
                        className={styles.transformativeSectionBlockItemTitle}
                      >
                        {item.title}
                      </h4>
                    </div>
                  )
                )}
              </Grid>
            </div>
          </section>
        )}

        {props.originalResponse.transformativesection && (
          <section className={styles.transformativeSectionImage}>
            <CustomImage
              src={props.originalResponse.transformativesection}
              layout="fill"
              objectFit="cover"
            />
          </section>
        )}

        {props.originalResponse.shapessizessection && (
          <section className={styles.shapesSizesSection}>
            <h2 className={styles.shapesSizesSectionTitle}>
              {props.originalResponse.shapessizessection.title}
            </h2>
            <div
              className={styles.shapesSizesSectionDetail}
              dangerouslySetInnerHTML={createMarkup(
                props.originalResponse.shapessizessection.detail
              )}
            ></div>
            <Grid columns={3}>
              {props.originalResponse.shape_sizes_section_blocks.map(
                (block: any, index: number) => (
                  <div key={index}>
                    <div className={styles.shapesSizesSectionImage}>
                      <CustomImage src={block} layout="responsive" />
                    </div>
                    <div className={styles.shapesSizesSectionContent}>
                      <div
                        className={styles.shapesSizesSectionContentDetail}
                        dangerouslySetInnerHTML={createMarkup(block.detail)}
                      ></div>
                    </div>
                  </div>
                )
              )}
            </Grid>
          </section>
        )}

        {props.originalResponse.imagequotesection && (
          <ImageQuote
            quote={props.originalResponse.imagequotesection.quote}
            author={props.originalResponse.imagequotesection.author}
            image={props.originalResponse.imagequotesection}
          />
        )}
      </div>
    </div>
  )
}

export async function getStaticProps() {
  const page: GenericPageLayoutProps = await parseGenericPage(
    `${getAPI()}/batterypage/kavian-technology/`
  )
  return {
    props: {
      originalResponse: page.originalResponse,
      globalData: page.globalData,
      title: page.title,
      // meta_title: page.meta_title,
      // meta_description: page.meta_description,
    },
    revalidate: NEXTJS_REVALIDATE_PAGE_IN_SECONDS,
  }
}

export const NEXTJS_REVALIDATE_PAGE_IN_SECONDS = 10
export default SolidStateBatteries
